import { queryKeys } from "@bso/shared";
import { createRoute } from "@tanstack/solid-router";
import { Link } from "@/lib/u-router";
import { appRoute } from "./route";
import { createMemo, createSignal, For, onCleanup, Show, Suspense } from "solid-js";
import {
  AlertCircle,
  BookOpen,
  CheckCircle,
  Download,
  HardDrive,
  RefreshCw,
  Settings as SettingsIcon,
} from "lucide-solid";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { bible_version, bible_version_file, type BibleVersionFile } from "@bso/powersync";

// Tauri detection and dynamic imports
const isTauri = () => typeof globalThis !== "undefined" && "__TAURI_INTERNALS__" in globalThis;

// Resolve backend base URL at module init time (Vite replaces VITE_* at build time)
const API_BASE: string = import.meta.env.VITE_API_BASE ?? "http://localhost:3333";

interface DownloadProgress {
  percent: number;
  bytesDownloaded: number;
  totalBytes: number;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) {
    return "0 B";
  }
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function SettingsPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  // Active downloads: versionId → progress (0–100)
  const [downloadProgress, setDownloadProgress] = createSignal<Record<string, DownloadProgress>>(
    {},
  );
  const [downloadErrors, setDownloadErrors] = createSignal<Record<string, string>>({});
  const [downloadDone, setDownloadDone] = createSignal<Record<string, boolean>>({});

  // Query bible versions from PowerSync local cache
  const versions = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.versions(userId()),
    db: () => db(),
    queryFn: () => {
      const drizzleDb = drizzle();
      if (!drizzleDb) {
        return null;
      }
      return drizzleDb.select().from(bible_version);
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()),
  });

  // Query version files for download URLs
  const versionFiles = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.versionFiles(),
    db: () => db(),
    queryFn: () => {
      const drizzleDb = drizzle();
      if (!drizzleDb) {
        return null;
      }
      return drizzleDb.select().from(bible_version_file);
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()),
  });

  // Map versionId → first file entry (typed)
  const filesByVersion = createMemo(() => {
    const map = new Map<string, BibleVersionFile>();
    for (const f of versionFiles.data ?? []) {
      const id = f.bible_version_id;
      if (typeof id === "string" && !map.has(id)) {
        map.set(id, f);
      }
    }
    return map;
  });

  async function handleDownload(versionId: string, abbr: string) {
    if (!isTauri()) {
      return;
    }

    const file = filesByVersion().get(versionId);
    if (!file?.relative_path) {
      setDownloadErrors((prev) => ({
        ...prev,
        [versionId]: t(msg`No download file found for this version.`),
      }));
      return;
    }

    // Clear previous state
    setDownloadErrors((prev) => {
      const { [versionId]: _, ...next } = prev;
      return next;
    });
    setDownloadDone((prev) => {
      const { [versionId]: _, ...next } = prev;
      return next;
    });
    setDownloadProgress((prev) => ({
      ...prev,
      [versionId]: { percent: 0, bytesDownloaded: 0, totalBytes: 0 },
    }));

    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const { listen } = await import("@tauri-apps/api/event");

      // Listen for progress events from the Rust streaming download
      const unlisten = await listen<DownloadProgress>(
        "download-progress",
        (event: { payload: DownloadProgress }) => {
          setDownloadProgress((prev) => ({
            ...prev,
            [versionId]: event.payload,
          }));
        },
      );

      try {
        // Build full URL from relative_path — use the backend API base
        const fileRecord = filesByVersion().get(versionId);
        const relativePath = fileRecord?.relative_path ?? null;
        const url = `${API_BASE}/uploads/${relativePath ?? ""}`;

        await invoke("download_bible_db", { url, abbr });

        setDownloadDone((prev) => ({ ...prev, [versionId]: true }));
        setDownloadProgress((prev) => {
          const { [versionId]: _, ...next } = prev;
          return next;
        });
      } finally {
        unlisten();
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setDownloadErrors((prev) => ({ ...prev, [versionId]: errMsg }));
      setDownloadProgress((prev) => {
        const { [versionId]: _, ...next } = prev;
        return next;
      });
    }
  }

  onCleanup(() => {
    setDownloadProgress({});
  });

  return (
    <div class="flex flex-col h-full w-full bg-background text-foreground overflow-hidden">
      {/* Header */}
      <div class="border-b border-border/40 bg-card/30 backdrop-blur-lg px-4 md:px-8 py-4 md:py-5 flex items-center gap-3 shrink-0">
        <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
          <SettingsIcon class="h-5 w-5" />
        </div>
        <div>
          <h1 class="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-transparent">
            {t(msg`Settings`)}
          </h1>
          <p class="text-xs text-muted-foreground mt-0.5">
            {t(msg`Management of Bible translations and offline storage`)}
          </p>
        </div>
      </div>

      {/* Content */}
      <div class="flex-1 overflow-y-auto p-4 md:p-8">
        <div class="max-w-3xl mx-auto w-full space-y-8">
          {/* Offline Bible Downloads Section */}
          <section class="space-y-4">
            <div class="flex items-center gap-3">
              <HardDrive class="h-5 w-5 text-primary" />
              <h2 class="text-base font-bold text-foreground">{t(msg`Offline Bible Library`)}</h2>
            </div>
            <p class="text-sm text-muted-foreground leading-relaxed">
              {t(
                msg`Download translations to read them without internet connection. Databases are saved in the application's local directory.`,
              )}
            </p>

            <Show when={!isTauri()}>
              <div class="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/25 text-sm text-amber-700 dark:text-amber-400">
                <AlertCircle class="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {t(
                    msg`Native offline download is only available in the desktop application (Tauri). In the web browser, access to translations is done through the server in real time.`,
                  )}
                </span>
              </div>
            </Show>

            <div class="space-y-3">
              <Suspense
                fallback={
                  <div class="space-y-3 animate-pulse">
                    <div class="h-20 bg-muted/60 rounded-xl" />
                    <div class="h-20 bg-muted/60 rounded-xl" />
                  </div>
                }
              >
                <Show
                  when={(versions.data ?? []).length > 0}
                  fallback={
                    <div class="flex flex-col items-center justify-center py-12 text-center border border-dashed border-border/40 rounded-2xl">
                      <BookOpen class="h-8 w-8 text-muted-foreground/40 mb-2" />
                      <p class="text-sm text-muted-foreground">
                        {t(msg`No translations available in the catalog.`)}
                      </p>
                    </div>
                  }
                >
                  <For each={versions.data ?? []}>
                    {(version) => {
                      const progress = () => downloadProgress()[version.id];
                      const error = () => downloadErrors()[version.id];
                      const isDone = () => downloadDone()[version.id];
                      const isDownloading = () => Boolean(progress());
                      const hasFile = () => filesByVersion().has(version.id);

                      return (
                        <div class="group relative flex items-center gap-4 p-5 rounded-2xl border border-border/30 bg-card/40 backdrop-blur-sm hover:bg-card/60 transition-all duration-200">
                          {/* Version Badge */}
                          <div class="shrink-0 w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                            <span class="text-xs font-black text-primary uppercase tracking-tight">
                              {version.abbr ?? "?"}
                            </span>
                          </div>

                          {/* Info */}
                          <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2 mb-0.5">
                              <span class="text-sm font-bold text-foreground truncate">
                                {version.name ?? version.abbr}
                              </span>
                              <Show when={version.language_iso2}>
                                <span class="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-muted/60 text-muted-foreground uppercase tracking-wide">
                                  {version.language_iso2}
                                </span>
                              </Show>
                            </div>

                            {/* Progress bar */}
                            <Show when={isDownloading()}>
                              <div class="mt-2 space-y-1">
                                <div class="w-full bg-muted/40 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    class="h-1.5 rounded-full bg-gradient-to-r from-primary to-primary/75 transition-all duration-300"
                                    style={{
                                      width: `${Math.min(progress()?.percent ?? 0, 100).toFixed(1)}%`,
                                    }}
                                  />
                                </div>
                                <div class="flex justify-between text-2xs text-muted-foreground">
                                  <span>
                                    {formatBytes(progress()?.bytesDownloaded ?? 0)} /{" "}
                                    {formatBytes(progress()?.totalBytes ?? 0)}
                                  </span>
                                  <span>{(progress()?.percent ?? 0).toFixed(0)}%</span>
                                </div>
                              </div>
                            </Show>

                            {/* Error */}
                            <Show when={error()}>
                              <p class="mt-1.5 text-2xs text-red-500 leading-tight">{error()}</p>
                            </Show>
                          </div>

                          {/* Actions */}
                          <div class="shrink-0 flex items-center gap-2">
                            {/* Start reading this version (legacy parity: version activation) */}
                            <Show when={version.abbr}>
                              {(abbr) => (
                                <Link
                                  to="/bible/$version/$bookAbbr/$chapter"
                                  params={{
                                    version: abbr().toLowerCase(),
                                    bookAbbr: "gen",
                                    chapter: "1",
                                  }}
                                  class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold border border-border/40 bg-background hover:bg-muted/40 transition-all duration-200 active:scale-95"
                                  title={(() => {
                                    const ver = abbr().toUpperCase();
                                    return t(msg`Read version ${ver}`);
                                  })()}
                                >
                                  <BookOpen class="w-3.5 h-3.5" />
                                  <span>{t(msg`Read`)}</span>
                                </Link>
                              )}
                            </Show>

                            <Show when={isTauri()}>
                              <Show
                                when={isDone()}
                                fallback={
                                  <Show
                                    when={isDownloading()}
                                    fallback={
                                      <button
                                        type="button"
                                        disabled={!hasFile()}
                                        onClick={async () => {
                                          if (version.id && version.abbr) {
                                            await handleDownload(version.id, version.abbr);
                                          }
                                        }}
                                        class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200"
                                        classList={{
                                          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md active:scale-95":
                                            hasFile(),
                                          "bg-muted/60 text-muted-foreground cursor-not-allowed":
                                            !hasFile(),
                                        }}
                                        title={
                                          hasFile()
                                            ? t(msg`Download for offline use`)
                                            : t(msg`No file available`)
                                        }
                                      >
                                        <Download class="w-3.5 h-3.5" />
                                        <span>{t(msg`Download`)}</span>
                                      </button>
                                    }
                                  >
                                    <div class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold text-muted-foreground bg-muted/40 border border-border/20">
                                      <RefreshCw class="w-3.5 h-3.5 animate-spin" />
                                      <span>{t(msg`Downloading...`)}</span>
                                    </div>
                                  </Show>
                                }
                              >
                                <div class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold text-success bg-success/10 border border-success/20">
                                  <CheckCircle class="w-3.5 h-3.5" />
                                  <span>{t(msg`Downloaded`)}</span>
                                </div>
                              </Show>
                            </Show>
                          </div>
                        </div>
                      );
                    }}
                  </For>
                </Show>
              </Suspense>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: SettingsPage,
});
